import React, { useEffect, useState } from "react";
import { PageHeader, Card, Badge, Button, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Flame, ArrowRight, Building2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const STAGE_TONE = {
  sourcing: "neutral",
  diligence: "gold",
  negotiation: "gold",
  closing: "green",
  closed: "green",
  exited: "navy",
};

export default function ConvergentWatchlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/origin/convergent-watchlist");
        setItems(r.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-4 md:p-10 max-w-6xl mx-auto">
      <PageHeader
        eyebrow="CLARA Origin · Convergent Watchlist"
        title={<>High-Conviction <span className="italic text-gold">Convergent</span> Targets</>}
        description="Every target that has ever surfaced in ≥3 origination theses across any Bulk Discovery run. Persistent history — convergences don't get lost across runs."
        testid="convergent-watchlist-page"
        actions={
          <Link to="/origin/bulk">
            <Button variant="outline" data-testid="back-to-bulk">
              <Flame size={13} /> Run Bulk Discovery
            </Button>
          </Link>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : items.length === 0 ? (
        <Card>
          <div className="text-center py-16">
            <Flame size={40} className="mx-auto text-white/20 mb-4" />
            <div className="text-white/60 mb-3">No convergent targets yet.</div>
            <div className="text-[12px] text-white/40 mb-6 max-w-md mx-auto">
              Run Bulk Discovery with 3+ overlapping theses to surface targets that convergent multiple lenses.
            </div>
            <Link to="/origin/bulk">
              <Button variant="gold" data-testid="empty-cta">
                <ArrowRight size={13} /> Go to Bulk Discovery
              </Button>
            </Link>
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="p-3 clara-surface-2 rounded-md">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Total Targets</div>
              <div className="text-2xl font-display text-white">{items.length}</div>
            </div>
            <div className="p-3 clara-surface-2 rounded-md" style={{ border: "1px solid rgba(197,160,89,0.4)" }}>
              <div className="text-[10px] uppercase tracking-wider text-gold/80 mb-1 flex items-center gap-1"><Flame size={11} /> Max Convergence</div>
              <div className="text-2xl font-display text-gold">×{Math.max(...items.map((i) => i.max_convergence || 0), 0)}</div>
            </div>
            <div className="p-3 clara-surface-2 rounded-md">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Repeat Appearances</div>
              <div className="text-2xl font-display text-white">{items.filter((i) => (i.total_appearances || 0) > 1).length}</div>
            </div>
            <div className="p-3 clara-surface-2 rounded-md">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Already in DD+</div>
              <div className="text-2xl font-display text-white">{items.filter((i) => ["diligence","negotiation","closing","closed"].includes(i.current_deal_stage)).length}</div>
            </div>
          </div>

          <Card>
            <div className="overline mb-3">Watchlist · Sorted by Last Seen</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="text-[10px] uppercase tracking-wider text-white/40 border-b border-white/5">
                  <tr>
                    <th className="text-left py-2 pl-2">Company</th>
                    <th className="text-left">Sector</th>
                    <th className="text-left">Country</th>
                    <th className="text-center">Max Convergence</th>
                    <th className="text-center">Appearances</th>
                    <th className="text-center">Deal Stage</th>
                    <th className="text-right pr-2">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={it.company_id || i} data-testid={`watchlist-row-${i}`} className="border-b border-white/[0.04] hover:bg-[#C5A059]/[0.05]">
                      <td className="py-2 pl-2 text-white font-medium flex items-center gap-1.5">
                        <Flame size={12} className="text-gold" />
                        {it.name}
                      </td>
                      <td className="text-white/70">{it.sector || "—"}</td>
                      <td className="text-white/60">{it.country || "—"}</td>
                      <td className="text-center">
                        <Badge tone="gold">×{it.max_convergence}</Badge>
                      </td>
                      <td className="text-center text-white/70 font-mono">{it.total_appearances || 1}</td>
                      <td className="text-center">
                        {it.current_deal_stage ? (
                          <Badge tone={STAGE_TONE[it.current_deal_stage] || "neutral"}>{it.current_deal_stage}</Badge>
                        ) : (
                          <span className="text-white/30 text-[10px]">no deal</span>
                        )}
                      </td>
                      <td className="text-right pr-2 text-white/50 font-mono text-[10px]">
                        {it.last_seen ? new Date(it.last_seen).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
